// edge-connector-spec.js — client-side builder for signed edge connector
// specs (GPIO, Modbus TCP/RTU/RTU-over-TCP, OPC UA, MQTT, EtherNet/IP Class 1, EtherCAT, PROFINET). Ported from the editor's
// plcTagConnection.js when tag-connection creation moved from the PLC
// binding cell to this gateway page; keep the two in sync if the canonical
// spec shape changes. Credentials are intentionally absent: authenticated
// profiles may reference provisioned edge secrets, but this workflow never
// accepts or transports secret material.

export const EDGE_CONNECTOR_KINDS = Object.freeze([
  { value: "gpio", label: "Built-in digital I/O", capability: "gpio" },
  { value: "modbus-tcp", label: "Modbus TCP", capability: "modbus-tcp-client" },
  { value: "modbus-rtu", label: "Modbus RTU", capability: "modbus-rtu-client" },
  { value: "modbus-rtu-over-tcp", label: "Modbus RTU over TCP", capability: "modbus-rtu-over-tcp-client" },
  {
    value: "ncd-pr63-27c-usb",
    label: "Control Seat machine health sensor",
    capabilities: ["ncd-pr63-27c-ethernet-receiver-v1", "ncd-pr63-27c-usb-receiver-v1"],
  },
  { value: "opcua", label: "OPC UA", capability: "opcua-client" },
  { value: "mqtt", label: "MQTT", capability: "mqtt-direct-topic-v1" },
  { value: "enip-class1", label: "EtherNet/IP Class 1", capability: "enip-class1-scanner" },
  { value: "ethercat", label: "EtherCAT", capability: "ethercat-master" },
  { value: "profinet", label: "PROFINET", capability: "profinet-io-controller" },
]);

export const modbusUnitIDBounds = (kind) => kind === "modbus-tcp"
  ? { min: 0, max: 255 }
  : { min: 1, max: 247 };

export const MODBUS_DEVICE_TEMPLATES = Object.freeze({
  generic: { label: "Generic Modbus device" },
  "turck-tben-s1-8dxp": {
    label: "Turck TBEN-S1-8DXP",
    inputRegister: 0,
    outputRegister: 2048,
    channels: 8,
  },
});

const MODBUS_REGISTER_WIDTHS = Object.freeze({
  bool: 1,
  int16: 1,
  uint16: 1,
  int32: 2,
  uint32: 2,
  float32: 2,
  int64: 4,
  uint64: 4,
  float64: 4,
  bcd16: 1,
  bcd32: 2,
});

export const GPIO_BOARD_PROFILES = Object.freeze({
  "raspberry-pi-4-model-b-40pin-bcm-v1": "Raspberry Pi 4 Model B · 40-pin header",
  "raspberry-pi-5-40pin-bcm-v1": "Raspberry Pi 5 · 40-pin header",
  "compulab-iot-link-dio-v1": "Compulab IOT-LINK · 24 V digital I/O",
});

export const gpioBoardCapability = (profile) => (
  Object.prototype.hasOwnProperty.call(GPIO_BOARD_PROFILES, String(profile || "").trim())
    ? `gpio-board-profile:${String(profile).trim()}`
    : ""
);

const MQTT_VALUE_TYPES = {
  boolean: "bool",
  integer: "int64",
  number: "float64",
  string: "string",
  json: "json",
};

// The runtime tag catalog speaks wire types; the spec builder speaks the
// platform's canonical tag types. Both directions are needed here because
// the tag's type comes FROM the runtime's catalog evidence.
const CATALOG_TO_TAG_TYPE = {
  bool: "boolean", int64: "integer", float64: "number",
  string: "string", bytes: "bytes", json: "json",
};

export const tagTypeFromCatalogEntry = (entry) =>
  CATALOG_TO_TAG_TYPE[String(entry?.data_type || "").trim().toLowerCase()] || "";

// edgeConnectorTagEligibility mirrors the runtime-identity gate the editor
// applied: the exact path must be proven by a FRESH bounded catalog from the
// selected runtime, and writes require the runtime to report it writable.
export const edgeConnectorTagEligibility = (payload, path, direction) => {
  const runtime = payload?.runtime && typeof payload.runtime === "object" ? payload.runtime : {};
  if (runtime.catalog_fresh !== true) {
    return { eligible: false, message: runtime.snapshot_error || "The selected runtime tag catalog is not fresh." };
  }
  const exactPath = String(path || "").trim();
  const entries = Array.isArray(payload?.catalog?.entries) ? payload.catalog.entries : [];
  const entry = entries.find((candidate) => candidate?.path === exactPath);
  if (!entry) {
    const absent = Array.isArray(payload?.inspection?.absent_paths) && payload.inspection.absent_paths.includes(exactPath);
    return {
      eligible: false,
      message: absent
        ? "This tag does not exist on the selected runtime. Deploy/provision it there first."
        : "This tag is not proven by the selected runtime's bounded catalog. Refresh the runtime evidence before review.",
    };
  }
  const tagType = tagTypeFromCatalogEntry(entry);
  if (!tagType) {
    return { eligible: false, message: `The runtime reports unsupported data type ${entry.data_type || "(unknown)"}.` };
  }
  if (direction === "output" && entry.writable !== true) {
    return { eligible: false, message: "The selected runtime tag is read only, so it cannot carry device writes." };
  }
  // `writable` is reported alongside eligibility so callers that DERIVE the
  // flow (rather than being told it) can decide without a second lookup. A
  // read-only tag is still eligible — it simply reads instead of commanding.
  return {
    eligible: true,
    tagType,
    writable: entry.writable === true,
    message: "Exact edge tag identity is fresh and eligible.",
  };
};

const FR202_SIGNAL_TYPES = Object.freeze({
  voltage_0_5: { unit: "V", minimum: 0, maximum: 5, terminal: "analog" },
  voltage_0_10: { unit: "V", minimum: 0, maximum: 10, terminal: "analog" },
  voltage_minus5_5: { unit: "V", minimum: -5, maximum: 5, terminal: "analog" },
  voltage_minus10_10: { unit: "V", minimum: -10, maximum: 10, terminal: "analog" },
  current_0_20ma: { unit: "mA", minimum: 0, maximum: 20, terminal: "analog" },
  current_4_20ma: { unit: "mA", minimum: 4, maximum: 20, terminal: "analog" },
  current_4_24ma: { unit: "mA", minimum: 4, maximum: 24, terminal: "analog" },
});

const FR202_MINIMUM_ANALOG_RAW_SPAN = 2 ** 20;

export function buildFR202AnalogConnectorSpec({ connectorID = "", desiredState = "running", terminal, signalType, pollIntervalMs = 500, tagPath, low, high }) {
  const normalizedTerminal = requireText(terminal, "Terminal").toUpperCase();
  const isRTD = normalizedTerminal === "RTD0" || normalizedTerminal === "RTD1";
  if (isRTD || signalType === "pt100" || signalType === "pt1000") {
    throw new Error("RTD setup is not available until sensor wiring and excitation are qualified.");
  }
  const signal = FR202_SIGNAL_TYPES[requireText(signalType, "Signal type")];
  if (!signal) throw new Error("Choose a supported signal type.");
  const isAnalog = /^AI[0-3]$/.test(normalizedTerminal);
  if (signal.terminal === "analog" && !isAnalog) {
    throw new Error("The signal type does not match this terminal.");
  }
  const lowRaw = Number(low?.raw);
  const highRaw = Number(high?.raw);
  const lowReference = Number(low?.reference);
  const highReference = Number(high?.reference);
  if (![lowRaw, highRaw, lowReference, highReference].every(Number.isFinite)) {
    throw new Error("Capture both calibration points and enter their known values.");
  }
  if (Math.abs(highRaw - lowRaw) < FR202_MINIMUM_ANALOG_RAW_SPAN) {
    throw new Error("The captured readings are too close together. Apply two stable reference values farther apart.");
  }
  if (lowReference < signal.minimum || lowReference > signal.maximum || highReference < signal.minimum || highReference > signal.maximum) {
    throw new Error(`Calibration values must be between ${signal.minimum} and ${signal.maximum} ${signal.unit}.`);
  }
  const minimumSpan = (signal.maximum - signal.minimum) * 0.1;
  if (Math.abs(highReference - lowReference) < minimumSpan) {
    throw new Error(`Use reference values at least ${minimumSpan} ${signal.unit} apart.`);
  }
  const fullPath = requireText(tagPath, "Tag path");
  return {
    id: String(connectorID || "").trim(),
    kind: "fr202_analog",
    name: `${normalizedTerminal} measurement`,
    desired_state: connectorDesiredState(desiredState),
    config: { hardware_profile: "onlogic-fr202-v1" },
    mappings: [{
      tag_path: fullPath,
      native_address: `fr202:${normalizedTerminal}`,
      native_config: {
        terminal: normalizedTerminal,
        signal_type: signalType,
        poll_interval_ms: integer(pollIntervalMs, "Poll interval", 100, 60000),
        low: { raw: lowRaw, reference: lowReference },
        high: { raw: highRaw, reference: highReference },
      },
      flow: "observe",
      data_type: "float64",
      verify_readback: false,
    }],
    resources: [
      { key: "fr202:adc", mode: "shared" },
      { key: `fr202:terminal:${normalizedTerminal.toLowerCase()}`, mode: "exclusive" },
    ],
    requirements: {
      profile: "onlogic-fr202-analog-v1",
      capabilities: ["connector-config-v1", "fr202-analog-input-v1"],
    },
  };
}

const requireText = (value, label) => {
  const text = String(value || "").trim();
  if (!text) throw new Error(`${label} is required.`);
  return text;
};

const integer = (value, label, minimum, maximum) => {
  const number = Number(value);
  if (!Number.isSafeInteger(number) || number < minimum || number > maximum) {
    throw new Error(`${label} must be between ${minimum} and ${maximum}.`);
  }
  return number;
};

const finiteNumber = (value, label) => {
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error(`${label} must be a finite number.`);
  return number;
};

const ncdSerialResourceKey = (port) => {
  const path = requireText(port, "USB receiver");
  // Keep this byte-for-byte identical to ncdusb.SerialResourceKey without
  // depending on TextEncoder, which is absent in some supported browsers and
  // in the Gateway's jsdom test runtime.
  const escaped = encodeURIComponent(path);
  const bytes = [];
  for (let index = 0; index < escaped.length;) {
    if (escaped[index] === "%") {
      bytes.push(Number.parseInt(escaped.slice(index + 1, index + 3), 16));
      index += 3;
    } else {
      bytes.push(escaped.charCodeAt(index));
      index += 1;
    }
  }
  let encoded = "";
  bytes.forEach((value) => {
    const lower = value >= 97 && value <= 122;
    const digit = value >= 48 && value <= 57;
    const safePunctuation = [47, 46, 95, 45].includes(value);
    encoded += lower || digit || safePunctuation
      ? String.fromCharCode(value)
      : `%${value.toString(16).padStart(2, "0")}`;
  });
  return `serial:${encoded}`;
};

export function buildNCDGen4USBConnectorSpec({
  connectorID = "", desiredState = "running", name = "Control Seat sensor receiver",
  serialPort, baudRate = 115200, apiMode = 1,
}) {
  const port = requireText(serialPort, "USB receiver");
  return {
    id: String(connectorID || "").trim(),
    kind: "ncd-pr63-27c-usb",
    name: requireText(name, "Connection name"),
    desired_state: connectorDesiredState(desiredState),
    config: {
      serial_port: port,
      baud_rate: integer(baudRate, "Baud rate", 1200, 4000000),
      api_mode: integer(apiMode, "API mode", 1, 2),
      sensors: [],
    },
    mappings: [],
    resources: [{ key: ncdSerialResourceKey(port), mode: "exclusive" }],
    requirements: {
      profile: "ncd-pr63-27c-usb-v1",
      capabilities: [
        "connector-config-v1", "edge-tag-catalog-v1", "edge-tag-identity-v1",
        "ncd-pr63-27c-usb-receiver-v1",
      ],
    },
  };
}

const ncdEthernetHost = (value) => {
  const host = requireText(value, "Receiver address");
  const parts = host.split(".");
  if (parts.length !== 4 || parts.some((part) => !/^\d{1,3}$/.test(part) || Number(part) > 255)) {
    throw new Error("Receiver address must be a private IPv4 address.");
  }
  const octets = parts.map(Number);
  const privateAddress = octets[0] === 10 ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168) ||
    (octets[0] === 169 && octets[1] === 254);
  if (!privateAddress || octets.every((part) => part === 0) || octets[0] === 127) {
    throw new Error("Receiver address must be a private IPv4 address.");
  }
  return octets.join(".");
};

export function buildNCDGen4EthernetConnectorSpec({
  connectorID = "", desiredState = "running", name = "Control Seat sensor receiver",
  host = "", port = 2101, autoDiscover = true, apiMode = 1,
}) {
  const receiverPort = integer(port, "Receiver port", 1, 65535);
  const discover = Boolean(autoDiscover);
  const receiverHost = discover ? "" : ncdEthernetHost(host);
  return {
    id: String(connectorID || "").trim(),
    kind: "ncd-pr63-27c-ethernet",
    name: requireText(name, "Connection name"),
    desired_state: connectorDesiredState(desiredState),
    config: {
      ...(discover ? { auto_discover: true } : { host: receiverHost }),
      port: receiverPort,
      api_mode: integer(apiMode, "API mode", 1, 2),
      sensors: [],
    },
    mappings: [],
    resources: [{
      key: discover ? "udp:0.0.0.0:13000:ncd-ethernet-discovery" : `tcp:${receiverHost}:${receiverPort}`,
      mode: "exclusive",
    }],
    requirements: {
      profile: "ncd-pr63-27c-ethernet-v1",
      capabilities: [
        "connector-config-v1", "edge-tag-catalog-v1", "edge-tag-identity-v1",
        "ncd-pr63-27c-ethernet-receiver-v1",
      ],
    },
  };
}

const connectorDesiredState = (value = "running") => {
  const state = String(value || "").trim().toLowerCase();
  if (state !== "running" && state !== "stopped") {
    throw new Error("The saved connection state is not supported. Refresh the connection and try again.");
  }
  return state;
};

const plcFlow = (direction, supportsReadback = true) => (
  direction === "output" ? (supportsReadback ? "bidirectional" : "command") : "observe"
);

// Go's signed profile validators use net/url.QueryEscape when they build the
// canonical native address. encodeURIComponent differs for spaces and the
// !'()* characters, so mirror QueryEscape exactly or a browser preview can
// create an address the edge runtime will (correctly) reject.
const encodedAddressPart = (value) => encodeURIComponent(String(value || "").trim())
  .replace(/[!'()*]/g, (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`)
  .replace(/%20/g, "+");

// A Modbus device owns its transport and slave address once, then exposes any
// number of points beneath it. Keeping that boundary explicit prevents the UI
// from creating two competing runtimes for the same RTU port/unit pair.
export function buildEdgeModbusConnectorSpec({
  kind,
  name,
  connectorID = "",
  desiredState = "running",
  settings = {},
  points = [],
}) {
  if (!["modbus-tcp", "modbus-rtu", "modbus-rtu-over-tcp"].includes(kind)) {
    throw new Error("Choose a supported Modbus transport.");
  }
  const displayName = requireText(name, "Device name");
  const transport = kind === "modbus-rtu" ? "rtu" : kind === "modbus-rtu-over-tcp" ? "rtu_over_tcp" : "tcp";
  const usesTCP = transport !== "rtu";
  const template = transport === "tcp" ? String(settings.deviceTemplate || "generic") : "generic";
  const turck = template === "turck-tben-s1-8dxp";
  if (!MODBUS_DEVICE_TEMPLATES[template]) throw new Error("Choose a supported Modbus device template.");

  // Native Modbus TCP carries the full one-byte Unit Identifier (0..255).
  // Serial RTU addressing, including RTU frames tunneled over TCP, remains
  // limited to addressable slaves 1..247.
  const unitIDBounds = modbusUnitIDBounds(kind);
  const unitID = integer(settings.unitId ?? 1, "Device address", unitIDBounds.min, unitIDBounds.max);
  const config = {
    transport,
    unit_id: unitID,
    timeout_ms: integer(settings.timeoutMs ?? 2000, "Timeout", 50, 60000),
    reconnect_min_ms: integer(settings.reconnectMinMs ?? 250, "Minimum reconnect delay", 50, 60000),
    reconnect_max_ms: integer(settings.reconnectMaxMs ?? 30000, "Maximum reconnect delay", 50, 300000),
  };
  if (config.reconnect_max_ms < config.reconnect_min_ms) {
    throw new Error("Maximum reconnect delay must be at least the minimum reconnect delay.");
  }
  let resource;
  if (usesTCP) {
    const host = requireText(settings.host, "Device host");
    const port = integer(settings.port ?? 502, "TCP port", 1, 65535);
    config.tcp = { host, port };
    resource = `tcp:${host.includes(":") ? `[${host}]` : host}:${port}`;
  } else {
    const port = requireText(settings.serialPort, "Serial port");
    const parity = String(settings.parity || "even").trim().toLowerCase();
    if (!["even", "odd", "none"].includes(parity)) throw new Error("Parity must be Even, Odd, or None.");
    config.rtu = {
      port,
      baud_rate: integer(settings.baudRate ?? 19200, "Baud rate", 300, 4000000),
      data_bits: integer(settings.dataBits ?? 8, "Data bits", 8, 8),
      stop_bits: integer(settings.stopBits ?? 1, "Stop bits", 1, 2),
      parity,
    };
    resource = `serial:${port}`;
  }

  if (!Array.isArray(points) || points.length === 0) {
    throw new Error("Add at least one Modbus point.");
  }
  const seenTags = new Set();
  const pointClaims = [];
  let requiresModbusClientV2 = transport === "rtu_over_tcp";
  const mappings = points.map((point, index) => {
    const pointNumber = index + 1;
    const fullPath = requireText(point?.tagPath, `Point ${pointNumber} tag`);
    const dataType = requireText(point?.tagType, `Point ${pointNumber} tag data type`);
    if (point?.direction !== "input" && point?.direction !== "output") {
      throw new Error(`Point ${pointNumber}: Choose whether to read from or write to the device.`);
    }
    const output = point?.direction === "output";
    const pointSettings = point?.settings && typeof point.settings === "object" ? point.settings : {};
    if (!["boolean", "integer", "number", "string"].includes(dataType)) {
      throw new Error(`Point ${pointNumber} requires a Boolean, Integer, Number, or String tag.`);
    }
    if (turck && dataType !== "boolean") {
      throw new Error("The Turck TBEN-S1-8DXP digital I/O template requires a Boolean tag.");
    }
    const requestedValueType = String(pointSettings.valueType || "").trim().toLowerCase();
    const valueType = dataType === "boolean"
      ? "bool"
      : dataType === "string"
        ? "string"
      : requestedValueType || (dataType === "integer" ? "int16" : output ? "uint16" : "float32");
    const integerValueTypes = new Set(["int16", "uint16", "int32", "uint32", "int64", "uint64", "bcd16", "bcd32"]);
    const floatValueTypes = new Set(["float32", "float64"]);
    if (dataType === "integer" && !integerValueTypes.has(valueType)) {
      throw new Error(`Point ${pointNumber}: Integer tags require an integer Modbus value format.`);
    }
    if (dataType === "number" && !integerValueTypes.has(valueType) && !floatValueTypes.has(valueType)) {
      throw new Error(`Point ${pointNumber}: Number tags require a numeric Modbus value format.`);
    }
    const scale = dataType === "number" ? finiteNumber(pointSettings.scale ?? 1, `Point ${pointNumber} scale`) : 1;
    const offset = dataType === "number" ? finiteNumber(pointSettings.offset ?? 0, `Point ${pointNumber} offset`) : 0;
    if (scale === 0) throw new Error(`Point ${pointNumber}: Scale must be non-zero.`);
    if (dataType === "number" && integerValueTypes.has(valueType) && scale === 1 && offset === 0) {
      throw new Error(`Point ${pointNumber}: Use a non-identity scale or offset when an integer register feeds a Number tag.`);
    }
    const stringLength = dataType === "string"
      ? integer(pointSettings.stringLength ?? 32, `Point ${pointNumber} string length`, 2, 250)
      : 0;
    if (dataType === "string" && stringLength % 2 !== 0) {
      throw new Error(`Point ${pointNumber}: String length must be an even number of bytes.`);
    }
    const defaultFunction = turck ? 3 : dataType === "boolean" ? 1 : 3;
    const functionCode = turck
      ? 3
      : integer(pointSettings.function === "" || pointSettings.function == null ? defaultFunction : pointSettings.function, `Point ${pointNumber} function code`, 1, 4);
    const registerBit = turck
      ? integer(pointSettings.channel ?? 0, `Point ${pointNumber} Turck channel`, 0, 7)
      : dataType === "boolean" && (functionCode === 3 || functionCode === 4)
        ? integer(pointSettings.bit ?? 0, `Point ${pointNumber} register bit`, 0, 15)
        : null;
    const readableFunctions = dataType === "boolean" ? (registerBit == null ? [1, 2] : [3, 4]) : [3, 4];
    if (!readableFunctions.includes(functionCode)) {
      throw new Error(`Point ${pointNumber}: ${dataType === "boolean" ? "Boolean" : "Numeric"} mappings require function ${dataType === "boolean" ? "1 or 2" : "3 or 4"}.`);
    }
    if (output && !((dataType === "boolean" && (functionCode === 1 || functionCode === 3)) || (dataType !== "boolean" && functionCode === 3))) {
      throw new Error(`Point ${pointNumber}: Writable Modbus mappings require a coil or holding register.`);
    }
    const addressBase = String(pointSettings.addressBase || "zero_based").trim().toLowerCase();
    if (!["zero_based", "one_based"].includes(addressBase)) {
      throw new Error(`Point ${pointNumber}: Choose zero-based or one-based address numbering.`);
    }
    const enteredAddress = turck
      ? (output ? MODBUS_DEVICE_TEMPLATES[template].outputRegister : MODBUS_DEVICE_TEMPLATES[template].inputRegister)
      : integer(pointSettings.address ?? (addressBase === "one_based" ? 1 : 0), `Point ${pointNumber} address`, addressBase === "one_based" ? 1 : 0, addressBase === "one_based" ? 65536 : 65535);
    const address = turck ? enteredAddress : enteredAddress - (addressBase === "one_based" ? 1 : 0);
    const nativeAddress = `fc${functionCode}:${address}:${valueType}${registerBit == null ? "" : `:bit${registerBit}`}`;
    const tagKey = fullPath.toLowerCase();
    if (seenTags.has(tagKey)) throw new Error(`Point ${pointNumber}: Each tag can be connected only once on this device.`);
    const registerWidth = functionCode <= 2 || registerBit != null ? 1 : dataType === "string" ? stringLength / 2 : MODBUS_REGISTER_WIDTHS[valueType];
    if (!registerWidth || address + registerWidth > 65536) {
      throw new Error(`Point ${pointNumber}: This value extends past the end of the Modbus register table.`);
    }
    if (output && registerWidth > 123) {
      throw new Error(`Point ${pointNumber}: Writable register values may span at most 123 registers.`);
    }
    const readLimit = functionCode <= 2 ? 2000 : 125;
    const maxReadGap = integer(pointSettings.maxReadGap ?? 0, `Point ${pointNumber} maximum read gap`, 0, readLimit - 1);
    const maxReadCountText = String(pointSettings.maxReadCount ?? "").trim();
    const maxReadCount = maxReadCountText
      ? integer(maxReadCountText, `Point ${pointNumber} maximum read count`, 1, readLimit)
      : 0;
    if (maxReadCount && registerWidth > maxReadCount) {
      throw new Error(`Point ${pointNumber}: The value width exceeds the maximum read count.`);
    }
    const inferredReadWindowMode = pointSettings.readWindowStart != null || pointSettings.readWindowCount != null
      ? "fixed"
      : "automatic";
    const readWindowMode = String(pointSettings.readWindowMode || inferredReadWindowMode).trim().toLowerCase();
    if (!["automatic", "fixed"].includes(readWindowMode)) {
      throw new Error(`Point ${pointNumber}: Choose automatic or fixed-block reads.`);
    }
    let readWindowStart = null;
    let readWindowCount = null;
    if (readWindowMode === "fixed") {
      const enteredReadWindowStart = integer(pointSettings.readWindowStart, `Point ${pointNumber} read block start`, addressBase === "one_based" ? 1 : 0, addressBase === "one_based" ? 65536 : 65535);
      readWindowStart = enteredReadWindowStart - (addressBase === "one_based" ? 1 : 0);
      readWindowCount = integer(pointSettings.readWindowCount, `Point ${pointNumber} read block size`, 1, readLimit);
      if (readWindowStart + readWindowCount > 65536) {
        throw new Error(`Point ${pointNumber}: The fixed read block extends past the end of the Modbus address table.`);
      }
      if (address < readWindowStart || address + registerWidth > readWindowStart + readWindowCount) {
        throw new Error(`Point ${pointNumber}: The point must fit inside its fixed read block.`);
      }
      if (maxReadCount && readWindowCount > maxReadCount) {
        throw new Error(`Point ${pointNumber}: The fixed read block exceeds the maximum read count.`);
      }
    }
    const claim = {
      functionCode,
      first: address,
      last: address + registerWidth - 1,
      bit: registerBit,
      tagPath: fullPath,
    };
    const overlap = pointClaims.find((existing) => {
      if (existing.functionCode !== claim.functionCode || existing.last < claim.first || claim.last < existing.first) return false;
      // Separate bits in the same register are independent. A whole-register
      // point overlaps every bit in that register.
      return !(existing.bit != null && claim.bit != null && existing.first === claim.first && existing.bit !== claim.bit);
    });
    if (overlap) {
      throw new Error(`Point ${pointNumber}: This point overlaps device registers already used by ${overlap.tagPath}.`);
    }
    seenTags.add(tagKey);
    pointClaims.push(claim);
    const failurePolicy = String(pointSettings.failurePolicy || "last_good").trim().toLowerCase();
    if (!["last_good", "zero_bad"].includes(failurePolicy)) {
      throw new Error(`Point ${pointNumber}: Choose a supported failure policy.`);
    }
    const writeConfirmation = String(pointSettings.writeConfirmation || "readback").trim().toLowerCase();
    if (!["readback", "echo"].includes(writeConfirmation)) {
      throw new Error(`Point ${pointNumber}: Choose a supported write confirmation.`);
    }
    const savedFlow = String(pointSettings.flow || "").trim().toLowerCase();
    if (savedFlow && !["observe", "command", "bidirectional"].includes(savedFlow)) {
      throw new Error(`Point ${pointNumber}: The saved data flow is not supported.`);
    }
    // Devices such as small relay boards may acknowledge a write but expose
    // no readable status point. In that explicit mode the mapping is command
    // only, so the runtime does not turn every successful command into a
    // permanent read timeout. Readback remains the safe default.
    const flow = savedFlow || plcFlow(point.direction, writeConfirmation === "readback");
    const commandCapable = flow === "command" || flow === "bidirectional";
    if (commandCapable && !output) {
      throw new Error(`Point ${pointNumber}: The saved data flow requires a writable tag and Modbus point.`);
    }
    // V1 is an immutable wire contract. Extended batching/window fields,
    // BCD/strings, RTU-over-TCP, and FC16/FC22 command semantics must select a
    // profile old runtimes reject before interpreting the mapping body.
    requiresModbusClientV2 = requiresModbusClientV2 ||
      valueType === "bcd16" || valueType === "bcd32" || valueType === "string" ||
      maxReadGap > 0 || maxReadCount > 0 || readWindowMode === "fixed" ||
      (commandCapable && functionCode === 3 && (registerBit != null || registerWidth > 1));
    return {
      tag_path: fullPath,
      native_address: nativeAddress,
      native_config: {
        function: functionCode,
        address,
        value_type: valueType,
        ...(registerBit == null ? {} : { bit: registerBit }),
        ...(dataType === "boolean" ? {} : {
          byte_order: String(pointSettings.byteOrder || "big"),
          word_order: String(pointSettings.wordOrder || "big"),
        }),
        ...(dataType === "string" ? {
          string_length: stringLength,
          string_raw: Boolean(pointSettings.stringRaw),
          string_right_justify: Boolean(pointSettings.stringRightJustify),
        } : {}),
        ...(dataType === "number" ? { scale, offset } : {}),
        poll_interval_ms: integer(pointSettings.pollIntervalMs ?? 100, `Point ${pointNumber} poll interval`, 50, 3600000),
        ...(maxReadGap ? { max_read_gap: maxReadGap } : {}),
        ...(maxReadCount ? { max_read_count: maxReadCount } : {}),
        failure_policy: failurePolicy,
        write_confirmation: writeConfirmation,
        ...(readWindowMode === "fixed" ? {
          read_window_start: readWindowStart,
          read_window_count: readWindowCount,
        } : {}),
        ...(String(pointSettings.alias || "").trim() ? { alias: String(pointSettings.alias).trim() } : {}),
      },
      flow,
      data_type: dataType === "boolean" ? "bool" : dataType === "integer" ? "int64" : dataType === "string" ? "string" : "float64",
      verify_readback: commandCapable && writeConfirmation === "readback",
    };
  });

  return {
    id: String(connectorID || "").trim(),
    name: displayName,
    desired_state: connectorDesiredState(desiredState),
    kind: "modbus",
    config,
    mappings,
    resources: [{ key: resource, mode: "shared" }],
    requirements: {
      profile: requiresModbusClientV2 ? "modbus-client-v2" : "modbus-client-v1",
      capabilities: ["connector-config-v1", ...(requiresModbusClientV2 ? ["modbus-client-v2"] : []), transport === "tcp"
        ? "modbus-tcp-client"
        : transport === "rtu_over_tcp" ? "modbus-rtu-over-tcp-client" : "modbus-rtu-client"],
    },
  };
}

// Build the exact public connector spec reviewed by the cloud.
export function buildEdgeConnectorSpec({
  kind,
  name,
  connectorID = "",
  desiredState = "running",
  tagPath,
  tagType,
  direction,
  replacesLegacyGPIOBindingID = "",
  settings = {},
}) {
  const fullPath = requireText(tagPath, "Tag path");
  const displayName = requireText(name, "Connection name");
  const dataType = requireText(tagType, "Tag data type");
  const output = direction === "output";
  const common = {
    id: String(connectorID || "").trim(),
    name: displayName,
    desired_state: connectorDesiredState(desiredState),
  };

  if (kind === "gpio") {
    if (dataType !== "boolean") throw new Error("Digital I/O connections require a boolean tag.");
    const boardProfile = requireText(settings.boardProfile, "Detected digital I/O profile");
    const boardCapability = gpioBoardCapability(boardProfile);
    if (!boardCapability) throw new Error("The selected runtime did not report a supported digital I/O profile.");
    const pin = integer(settings.bcmPin, "Digital point", 0, 27);
    const availablePins = Array.isArray(settings.availablePins) ? settings.availablePins : [];
    const selectedPin = availablePins.find((candidate) => Number(candidate?.bcm) === pin && candidate?.policy !== "reserved");
    if (!selectedPin) {
      throw new Error("That digital point is not available on the detected device.");
    }
    const iotLink = boardProfile === "compulab-iot-link-dio-v1";
    const hardwareDirection = output ? "output" : "input";
    const native = {
      bcm_pin: pin,
      direction: hardwareDirection,
      bias: output || iotLink ? "disabled" : String(settings.bias || "pull_up"),
      active_low: !!settings.activeLow,
      debounce_ms: output ? 0 : integer(settings.debounceMs ?? 50, "Debounce", 0, 5000),
      safe_state: !!settings.safeState,
    };
    return {
      ...common,
      kind: "gpio",
      config: {
        board_profile: boardProfile,
        ...(replacesLegacyGPIOBindingID
          ? { replaces_legacy_gpio_binding_id: String(replacesLegacyGPIOBindingID).trim() }
          : {}),
      },
      mappings: [{
        tag_path: fullPath,
        native_address: iotLink ? `dio:${Number(selectedPin.physical) - 1}` : `bcm:${pin}`,
        native_config: native,
        flow: plcFlow(direction),
        data_type: "bool",
        verify_readback: output,
      }],
      resources: [{ key: iotLink ? `gpio:dio:${Number(selectedPin.physical) - 1}` : `gpio:bcm:${pin}`, mode: "exclusive" }],
      requirements: { profile: "gpio-bcm-v1", capabilities: ["connector-config-v1", "gpio", boardCapability] },
    };
  }

  if (["modbus-tcp", "modbus-rtu", "modbus-rtu-over-tcp"].includes(kind)) {
    return buildEdgeModbusConnectorSpec({
      kind,
      name: displayName,
      connectorID,
      settings,
      points: [{ tagPath: fullPath, tagType: dataType, direction, settings }],
    });
  }

  if (kind === "opcua") {
    const endpoint = requireText(settings.endpointUrl, "Endpoint URL");
    const nodeID = requireText(settings.nodeId, "Node ID");
    const namespaceURI = String(settings.namespaceUri || "").trim();
    const certificate = requireText(settings.serverCertPEM, "Pinned server certificate");
    if (output && dataType === "json") {
      throw new Error("OPC UA JSON mappings are observe-only; use a command-capable MQTT or manual tag for compound writes.");
    }
    return {
      ...common,
      kind: "opcua",
      config: {
        endpoint_url: endpoint,
        security_mode: "sign_and_encrypt",
        security_policy: "Basic256Sha256",
        auth_mode: "anonymous",
        server_cert_pem: certificate,
      },
      mappings: [{
        tag_path: fullPath,
        native_address: `namespace_uri=${encodedAddressPart(namespaceURI)};node_id=${encodedAddressPart(nodeID)}`,
        native_config: { node_id: nodeID, ...(namespaceURI ? { namespace_uri: namespaceURI } : {}) },
        flow: plcFlow(direction),
        data_type: dataType === "boolean" ? "bool" : dataType === "integer" ? "int64" : dataType === "number" ? "float64" : dataType,
        verify_readback: output,
      }],
      resources: [{ key: `opcua:${endpoint}`, mode: "shared" }],
      requirements: { profile: "opcua-client-v1", capabilities: ["connector-config-v1", "opcua-client"] },
    };
  }

  if (kind === "mqtt") {
    const url = requireText(settings.url, "Broker URL");
    if (!/^(mqtts|ssl|tls|wss):\/\//i.test(url)) {
      throw new Error("MQTT edge connections require a TLS broker URL.");
    }
    const clientID = requireText(settings.clientId, "Client ID");
    const topic = requireText(settings.topic, "Topic");
    const valuePath = String(settings.valuePath || "").trim();
    if (output && valuePath) {
      throw new Error("MQTT JSON value-path mappings are observe-only because partial-document writes are not supported.");
    }
    const ca = requireText(settings.tlsCAPEM, "TLS CA certificate");
    const wireType = MQTT_VALUE_TYPES[dataType];
    if (!wireType) throw new Error("This tag type cannot map to a direct MQTT value.");
    return {
      ...common,
      kind: "mqtt",
      config: {
        url,
        client_id: clientID,
        keep_alive_sec: 30,
        clean_start: false,
        tls_enabled: true,
        tls_ca_pem: ca,
      },
      mappings: [{
        tag_path: fullPath,
        native_address: `topic=${encodedAddressPart(topic)};value_path=${encodedAddressPart(valuePath)}`,
        native_config: { topic, qos: 1, value_type: wireType, ...(valuePath ? { value_path: valuePath } : {}) },
        flow: plcFlow(direction, false),
        data_type: wireType,
        verify_readback: false,
      }],
      resources: [{ key: `mqtt-client:${url}:${clientID}`, mode: "exclusive" }],
      requirements: { profile: "mqtt-direct-v1", capabilities: ["connector-config-v1", "mqtt-direct-topic-v1"] },
    };
  }

  if (kind === "enip-class1") {
    if (dataType !== "boolean") {
      throw new Error("The Turck EtherNet/IP Class 1 digital I/O profile requires a Boolean tag.");
    }
    const host = requireText(settings.host, "Device host");
    const tcpPort = integer(settings.tcpPort ?? 44818, "TCP port", 1, 65535);
    const targetUDPPort = integer(settings.targetUDPPort ?? 2222, "I/O UDP port", 1, 65535);
    const channel = integer(settings.channel ?? 0, "Digital channel", 0, 7);
    const originatorVendorID = integer(settings.originatorVendorId, "Originator vendor ID", 1, 65535);
    const rpiMs = integer(settings.rpiMs ?? 10, "Cycle interval", 1, 1000);
    const point = output
      ? { direction: "output", byteOffset: 2 }
      : { direction: "input", byteOffset: 0 };
    const nativeAddress = `${point.direction}:byte${point.byteOffset}:bit${channel}:bool`;
    return {
      ...common,
      kind: "enip-class1",
      config: {
        host,
        tcp_port: tcpPort,
        target_udp_port: targetUDPPort,
        local_udp_host: String(settings.localUDPHost || "0.0.0.0").trim(),
        rpi_ms: rpiMs,
        operation_timeout_ms: integer(settings.operationTimeoutMs ?? 1000, "Operation timeout", 100, 30000),
        timeout_multiplier: integer(settings.timeoutMultiplier ?? 1, "Timeout multiplier", 0, 7),
        reconnect_min_ms: 250,
        reconnect_max_ms: 30000,
        originator_vendor_id: originatorVendorID,
        device_profile: {
          id: "turck-tben-s1-8dxp",
          label: "Turck TBEN-S1-8DXP",
          identity: { vendor_id: 48, device_type: 12, product_code: 14023, major_revision: 2 },
          configuration_assembly: 1,
          output_assembly: 104,
          input_assembly: 103,
          output_payload_bytes: 12,
          input_payload_bytes: 20,
          output_has_run_idle_header: true,
          input_has_run_idle_header: false,
        },
        safe_output_base64: "AAAAAAAAAAAAAAAA",
      },
      mappings: [{
        tag_path: fullPath,
        native_address: nativeAddress,
        native_config: {
          direction: point.direction,
          field: { byte_offset: point.byteOffset, bit_offset: channel, bit_width: 1, kind: "bool" },
        },
        flow: output ? "command" : "observe",
        data_type: "bool",
        verify_readback: false,
      }],
      resources: [{ key: `enip-class1:${host.includes(":") ? `[${host}]` : host}:${tcpPort}`, mode: "shared" }],
      requirements: { profile: "enip-class1-scanner-v1", capabilities: ["connector-config-v1", "enip-class1-scanner"] },
    };
  }

  if (kind === "ethercat") {
    if (dataType !== "boolean") {
      throw new Error("The Beckhoff EK1818 EtherCAT digital I/O profile requires a Boolean tag.");
    }
    const interfaceName = requireText(settings.interfaceName, "Network interface");
    if (!/^[A-Za-z0-9_.-]{1,15}$/.test(interfaceName)) {
      throw new Error("Network interface must be a Linux interface name such as enp2s0.");
    }
    const channel = integer(settings.channel ?? 1, "Digital channel", 1, output ? 4 : 8);
    const bit = channel - 1;
    const pointDirection = output ? "output" : "input";
    return {
      ...common,
      kind: "ethercat",
      config: {
        interface: interfaceName,
        station_address: integer(settings.stationAddress ?? 4097, "Station address", 1, 65535),
        logical_address: integer(settings.logicalAddress ?? 65536, "Logical address", 1, 4294967295),
        cycle_time_ms: integer(settings.cycleTimeMs ?? 10, "Cycle interval", 1, 1000),
        watchdog_ms: integer(settings.watchdogMs ?? 100, "Watchdog", 2, 60000),
        startup_timeout_ms: integer(settings.startupTimeoutMs ?? 3000, "Startup timeout", 2, 60000),
        publish_interval_ms: integer(settings.publishIntervalMs ?? 100, "Publish interval", 1, 60000),
        reconnect_min_ms: 250,
        reconnect_max_ms: 30000,
        device_model: "beckhoff-ek1818",
        device_revision: requireText(settings.deviceRevision || "0x00120000", "Device revision"),
        safe_output_base64: "AA==",
      },
      mappings: [{
        tag_path: fullPath,
        native_address: `${pointDirection}:byte0:bit${bit}:bool`,
        native_config: { direction: pointDirection, channel },
        flow: output ? "command" : "observe",
        data_type: "bool",
        verify_readback: false,
      }],
      resources: [{ key: `ethercat:${interfaceName}`, mode: "shared" }],
      requirements: { profile: "ethercat-master-v1", capabilities: ["connector-config-v1", "ethercat-master"] },
    };
  }

  if (kind === "profinet") {
    if (dataType !== "boolean") {
      throw new Error("The Turck TBEN-S1-8DXP PROFINET digital I/O profile requires a Boolean tag.");
    }
    const interfaceName = requireText(settings.interfaceName, "Network interface");
    if (!/^[A-Za-z0-9_.-]{1,15}$/.test(interfaceName)) {
      throw new Error("Network interface must be a Linux interface name such as enp2s0.");
    }
    const stationName = requireText(settings.stationName, "PROFINET station name");
    if (stationName.length > 240 || stationName !== stationName.toLowerCase()
      || !stationName.split(".").every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label))) {
      throw new Error("PROFINET station name must be a lowercase DNS-compatible name.");
    }
    const channel = integer(settings.channel ?? 1, "Digital channel", 1, 8);
    const cycleTimeMs = integer(settings.cycleTimeMs ?? 32, "Cycle interval", 1, 512);
    if (![1, 2, 4, 8, 16, 32, 64, 128, 256, 512].includes(cycleTimeMs)) {
      throw new Error("PROFINET cycle interval must be 1, 2, 4, 8, 16, 32, 64, 128, 256, or 512 ms.");
    }
    const bit = channel - 1;
    const pointDirection = output ? "output" : "input";
    return {
      ...common,
      kind: "profinet",
      config: {
        interface: interfaceName,
        station_name: stationName,
        send_clock: 32,
        reduction_ratio: cycleTimeMs,
        watchdog_ms: integer(settings.watchdogMs ?? 320, "Watchdog", 2, 60000),
        startup_timeout_ms: integer(settings.startupTimeoutMs ?? 5000, "Startup timeout", 2, 60000),
        publish_interval_ms: integer(settings.publishIntervalMs ?? 100, "Publish interval", 1, 60000),
        reconnect_min_ms: 250,
        reconnect_max_ms: 30000,
        device_model: "turck-tben-s1-8dxp",
        safe_output_base64: "AA==",
      },
      mappings: [{
        tag_path: fullPath,
        native_address: `${pointDirection}:byte0:bit${bit}:bool`,
        native_config: { direction: pointDirection, channel },
        flow: output ? "command" : "observe",
        data_type: "bool",
        verify_readback: false,
      }],
      resources: [{ key: `profinet:${interfaceName}:${stationName}`, mode: "shared" }],
      requirements: { profile: "profinet-controller-v1", capabilities: ["connector-config-v1", "profinet-io-controller"] },
    };
  }

  throw new Error(`Unsupported edge connector kind ${kind || "(empty)"}.`);
}
